import { Settings2 } from "lucide-react";
import { SegmentedControl } from "../ui/SegmentedControl";
import { BIKE_TYPE_TIRE_DEFAULTS } from "../../types/rider-input";

const FONT_DISPLAY = "var(--gripr-font-display)";
const TEXT_MUTED = "var(--gripr-text-muted)";

interface BikeSetupFormProps {
  bikeType: string;
  onBikeTypeChange: (v: string) => void;
  bikeTypeError?: string;
  tireFront: number | undefined;
  tireFrontError?: string;
  onTireFrontChange: (v: number | undefined) => void;
  tireRear: number | undefined;
  tireRearError?: string;
  onTireRearChange: (v: number | undefined) => void;
  wheelSize: string;
  onWheelSizeChange: (v: string) => void;
  bikeWeight: number;
  onBikeWeightChange: (v: number) => void;
  bikeWeightError?: string;
  tubeless: boolean;
  onTubelessChange: (v: boolean) => void;
  tireInserts: boolean;
  onTireInsertsChange: (v: boolean) => void;
  disabled?: boolean;
}

function GriprSwitch({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={`gripr-switch-track ${checked ? "on" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
      role="switch"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) =>
        !disabled && (e.key === "Enter" || e.key === " ") && onChange(!checked)
      }
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div className="gripr-switch-thumb" />
    </div>
  );
}

export function BikeSetupForm({
  bikeType,
  onBikeTypeChange,
  bikeTypeError,
  tireFront,
  tireFrontError,
  onTireFrontChange,
  tireRear,
  tireRearError,
  onTireRearChange,
  wheelSize,
  onWheelSizeChange,
  bikeWeight,
  onBikeWeightChange,
  bikeWeightError,
  tubeless,
  onTubelessChange,
  tireInserts,
  onTireInsertsChange,
  disabled = false,
}: BikeSetupFormProps) {
  const defaultTireWidth =
    BIKE_TYPE_TIRE_DEFAULTS[bikeType as keyof typeof BIKE_TYPE_TIRE_DEFAULTS]
      ?.front ?? 2.4;

  return (
    <div className="gripr-card">
      <div className="gripr-card-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="gripr-section-icon">
            <Settings2 size={18} strokeWidth={2} />
          </div>
          <div>
            <div className="gripr-section-label" style={{ marginBottom: 0 }}>
              Step 3
            </div>
            <h3
              style={{
                margin: 0,
                color: "var(--gripr-text-primary)",
                fontFamily: FONT_DISPLAY,
              }}
            >
              Bike Setup
            </h3>
          </div>
        </div>
      </div>

      <div className="gripr-card-body">
        <div
          style={{
            display: "grid",
             gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "1.25rem",
          }}
        >
          <div className="gripr-form-group" style={{ marginBottom: 0 }}>
            <label className="gripr-label">
              Bike Type
              <span
                style={{
                  color: "rgb(255, 140, 85)",
                  marginLeft: 4,
                  fontWeight: 600,
                }}
              >
                *
              </span>
            </label>
            <select
              className="gripr-select"
              value={bikeType || ""}
              onChange={(e) => onBikeTypeChange(e.target.value)}
              disabled={disabled}
              style={{ borderColor: bikeTypeError ? "#dc3545" : undefined }}
            >
              <option value="" disabled>
                Please select
              </option>
              <option value="xc">XC — Cross Country</option>
              <option value="trail">Trail</option>
              <option value="enduro">Enduro</option>
              <option value="downhill">Downhill</option>
              <option value="gravel">Gravel</option>
            </select>
            {bikeTypeError ? (
              <div
                style={{
                  marginTop: "0.4rem",
                  fontSize: "0.75rem",
                  color: "#dc3545",
                }}
              >
                {bikeTypeError}
              </div>
            ) : (
              <div
                style={{
                  marginTop: "0.4rem",
                  fontSize: "0.75rem",
                  color: TEXT_MUTED,
                }}
              >
                Choose your bike type for optimal pressure recommendations
              </div>
            )}
          </div>

          <div className="gripr-form-group" style={{ marginBottom: 0 }}>
            <label className="gripr-label">Bike Weight</label>
            <div className="gripr-input-wrap"><input type="number" className="gripr-input" value={bikeWeight || ""} onChange={(e) => onBikeWeightChange(Number(e.target.value))} min={5} max={45} step={0.1} disabled={disabled} style={{ paddingRight: "2.8rem", borderColor: bikeWeightError ? "#dc3545" : undefined }} /><span className="gripr-input-unit">kg</span></div>
            <div style={{ marginTop: "0.4rem", fontSize: "0.75rem", color: bikeWeightError ? "#dc3545" : TEXT_MUTED }}>{bikeWeightError ?? "Complete bike only, including installed mechanical components"}</div>
          </div>

          <div className="gripr-form-group" style={{ marginBottom: 0 }}>
            <label className="gripr-label">Wheel Size</label>
            <SegmentedControl
              options={['27.5"', '29"']}
              value={wheelSize}
              onChange={onWheelSizeChange}
              disabled={disabled}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1.25rem",
            marginTop: "1.25rem",
          }}
        >
          <div className="gripr-form-group" style={{ marginBottom: 0 }}>
            <label className="gripr-label">Front Tire Width</label>
            <div className="gripr-input-wrap">
              <input
                type="number"
                className="gripr-input"
                value={tireFront || ""}
                onChange={(e) =>
                  onTireFrontChange(
                    e.target.value === "" ? undefined : Number(e.target.value),
                  )
                }
                placeholder={defaultTireWidth.toString()}
                min={1.5}
                max={3.0}
                step={0.05}
                disabled={disabled}
                style={{
                  paddingRight: "3.2rem",
                  borderColor: tireFrontError ? "#dc3545" : undefined,
                }}
              />
              <span className="gripr-input-unit">inches</span>
            </div>
            {tireFrontError ? (
              <div
                style={{
                  marginTop: "0.4rem",
                  fontSize: "0.75rem",
                  color: "#dc3545",
                }}
              >
                {tireFrontError}
              </div>
            ) : (
              <div
                style={{
                  marginTop: "0.4rem",
                  fontSize: "0.75rem",
                  color: TEXT_MUTED,
                }}
              >
                Optional — defaults based on bike type
              </div>
            )}
          </div>

          <div className="gripr-form-group" style={{ marginBottom: 0 }}>
            <label className="gripr-label">Rear Tire Width</label>
            <div className="gripr-input-wrap">
              <input
                type="number"
                className="gripr-input"
                value={tireRear || ""}
                onChange={(e) =>
                  onTireRearChange(
                    e.target.value === "" ? undefined : Number(e.target.value),
                  )
                }
                placeholder={defaultTireWidth.toString()}
                min={1.5}
                max={3.0}
                step={0.05}
                disabled={disabled}
                style={{
                  paddingRight: "3.2rem",
                  borderColor: tireRearError ? "#dc3545" : undefined,
                }}
              />
              <span className="gripr-input-unit">inches</span>
            </div>
            {tireRearError ? (
              <div
                style={{
                  marginTop: "0.4rem",
                  fontSize: "0.75rem",
                  color: "#dc3545",
                }}
              >
                {tireRearError}
              </div>
            ) : (
              <div
                style={{
                  marginTop: "0.4rem",
                  fontSize: "0.75rem",
                  color: TEXT_MUTED,
                }}
              >
                Optional — defaults based on bike type
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            marginTop: "1.25rem",
          }}
        >
          <div className="gripr-toggle-row">
            <div>
              <div className="gripr-toggle-label">Tubeless Setup</div>
              <div className="gripr-toggle-desc">
                {tubeless
                  ? "Tubeless setup allows lower pressures with added puncture protection"
                  : "Tube setup requires slightly higher minimum pressures"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  fontFamily: FONT_DISPLAY,
                  color: tubeless ? "var(--gripr-accent)" : TEXT_MUTED,
                }}
              >
                {tubeless ? "YES" : "NO"}
              </span>
              <GriprSwitch
                checked={tubeless}
                onChange={onTubelessChange}
                disabled={disabled}
              />
            </div>
          </div>

          <div className="gripr-toggle-row">
            <div>
              <div className="gripr-toggle-label">Tire Inserts</div>
              <div className="gripr-toggle-desc">
                {tireInserts
                  ? "Tire inserts allow lower pressures with added rim protection"
                  : "No tire inserts, standard pressure recommendations apply"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  fontFamily: FONT_DISPLAY,
                  color: tireInserts ? "var(--gripr-accent)" : TEXT_MUTED,
                }}
              >
                {tireInserts ? "YES" : "NO"}
              </span>
              <GriprSwitch
                checked={tireInserts}
                onChange={onTireInsertsChange}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
